// CONTRACT:C4-DESIGN-TOKENS.1.0
/** Shipped themes as DATA — a complete primitive value set for every semantic slot.
 *  Swapping a Theme at runtime re-points CSS variables with NO recompile. These are
 *  bespoke tactical looks (deliberately NOT an off-the-shelf kit default):
 *    - "dark"          — the primary dark tactical theme (low-glow, night-watch).
 *    - "high-contrast" — raised-contrast surfaces for accessibility / bright-light ops.
 *  Both keep the status-* affiliation semantics intact (single source of affiliation color). */

import type { Theme } from "../../contracts-ts/src/index.ts";
import { APP6_AFFILIATION_COLORS } from "./status.ts";

/** Shared, non-color primitive values reused across themes (type/spacing/motion are
 *  structurally identical; themes differ in color/elevation). Authored once, spread in. */
const STRUCTURAL_TOKENS: Readonly<Record<string, string>> = Object.freeze({
  "typography-scale-xs": "11px",
  "typography-scale-sm": "13px",
  "typography-scale-md": "15px",
  "typography-scale-lg": "19px",
  "typography-scale-xl": "25px",
  // Canon font trio (tak-design-system Design System.dc.html §03), loaded via
  // Google Fonts in apps/cop/index.html. sans→Public Sans (UI/prose),
  // hud/display→Saira (HUD/labels/numerics), mono→IBM Plex Mono (envelope
  // fields/coordinates/crypto). System fallbacks keep text legible offline/DDIL.
  "typography-family-sans":
    "'Public Sans', 'Segoe UI', system-ui, sans-serif",
  "typography-family-mono":
    "'IBM Plex Mono', 'SF Mono', ui-monospace, monospace",
  "typography-family-hud":
    "'Saira', 'Public Sans', system-ui, sans-serif",
  "spacing-xs": "4px",
  "spacing-sm": "8px",
  "spacing-md": "12px",
  "spacing-lg": "20px",
  "spacing-xl": "32px",
  // Corner-radius scale (tak-design-system §06): tight technical radii, theme-invariant.
  // chip (small chips/badges) / control (buttons/inputs/toggles) / panel (floating surfaces).
  "radius-chip": "4px",
  "radius-control": "7px",
  "radius-panel": "12px",
  "motion-duration-instant": "0ms",
  "motion-duration-fast": "120ms",
  "motion-duration-slow": "320ms",
  "motion-easing-standard": "cubic-bezier(0.2, 0, 0, 1)",
  "motion-easing-decelerate": "cubic-bezier(0, 0, 0, 1)",
});

/** Semantic verdict/state hues — POLICY-OUTCOME chrome (KAS verdict, Trust modal, census,
 *  staleness), shared by every shipped theme. Canon Grant/Deny/Caution (tak-design-system §01).
 *  These are policy chrome, NOT affiliation, so they ride canon hues even under the mono-NVG
 *  hue-strip theme (whose discipline is about the affiliation FRAME on the map, not modal chrome). */
const SEMANTIC_TOKENS: Readonly<Record<string, string>> = Object.freeze({
  "color-semantic-grant": "#46D98E",
  "color-semantic-deny": "#E5484D",
  "color-semantic-caution": "#F2C14E",
});

/** Ambient CHROME ramp for the DARK-family themes — the neutral text + hairline layer the
 *  shell draws ON TOP of the dark surfaces. These are the EXACT values the COP shell :root used
 *  to hardcode (apps/cop/src/style.css), promoted to tokens so a LIGHT theme can re-point them.
 *  The shell :root was theme-agnostic, so every dark theme rendered this same ramp — shared here
 *  to preserve that byte-for-byte. (`--ui-accent` stays derived from color-interactive-hover in
 *  the shell :root; it is not a slot, so it is omitted here.) */
const AMBIENT_CHROME_DARK: Readonly<Record<string, string>> = Object.freeze({
  "ui-text": "#e6ebf1",
  "ui-text-dim": "#8a97a6",
  "ui-text-faint": "#5a6571",
  "ui-hairline": "rgba(125, 145, 165, 0.16)",
  "ui-hairline-soft": "rgba(216, 222, 230, 0.07)",
});

/** Ambient CHROME ramp for the LIGHT theme — a dark slate text ramp + dark hairlines drawn ON
 *  TOP of the light surfaces (the inverse of AMBIENT_CHROME_DARK). Tuned for legibility over the
 *  near-white panel glass and the CARTO Positron basemap (no white-on-white). */
const AMBIENT_CHROME_LIGHT: Readonly<Record<string, string>> = Object.freeze({
  "ui-text": "#16202c",
  "ui-text-dim": "#4a5765",
  "ui-text-faint": "#7c8896",
  "ui-hairline": "rgba(40, 55, 75, 0.18)",
  "ui-hairline-soft": "rgba(40, 55, 75, 0.08)",
});

/** The five APP-6 affiliation color slots, shared by every shipped theme so that
 *  status-* remains the single source of affiliation color across themes. */
const STATUS_TOKENS: Readonly<Record<string, string>> = Object.freeze({
  "status-hostile": APP6_AFFILIATION_COLORS.hostile,
  "status-friendly": APP6_AFFILIATION_COLORS.friendly,
  "status-neutral": APP6_AFFILIATION_COLORS.neutral,
  "status-unknown": APP6_AFFILIATION_COLORS.unknown,
  "status-pending": APP6_AFFILIATION_COLORS.pending,
});

/** LABEL PLATE — the protective translucent backing the map draws behind every callsign label
 *  (deckBackend buildCallsigns; alpha lives in render tuning, labelPlateAlpha). Per-theme
 *  because the plate must be the COMPLEMENTARY ground for that theme's basemap: near-black
 *  under the dark-matter basemap themes (the affiliation hues are bright — a dark plate lifts
 *  them off a saturated icon pile), WHITE under the light/Positron themes (Will: "with white
 *  background / maps, maybe 50% opacity white"). Text stays the affiliation hue everywhere
 *  (single-source channel law); on the light plates the thin dark SDF outline is what
 *  guarantees the lighter hues (unknown amber #F2C14E, pending orange #FF9E40) keep reading —
 *  documented constraint, since darkening the text itself would fork the affiliation palette.
 *  NOTE: deliberately a SHIPPED-THEME EXTRA token, not a semantic SLOT — adding a slot would
 *  invalidate every external/coalition skin (Theme completeness MUST); a skin without it falls
 *  back to the near-black default inside the backend (fail-to-default). */
export const LABEL_PLATE_TOKEN = "label-plate";

/** Bespoke DARK TACTICAL theme — deep desaturated slate surfaces, low-glow interactive. */
export const DARK_THEME: Theme = {
  id: "dark",
  tokens: {
    "color-surface-base": "#0B0F14",
    "color-surface-raised": "#141A22",
    "color-surface-overlay": "#1E2730",
    "color-surface-sunken": "#05080B",
    "color-interactive-default": "#2D6FA0",
    "color-interactive-hover": "#3A86BE",
    "color-interactive-active": "#4FA3DE",
    "color-interactive-disabled": "#2A323B",
    // Near-black cool plate (the canon label-outline near-black rgb(6,9,14)) — dark basemap.
    [LABEL_PLATE_TOKEN]: "#06090E",
    "elevation-flat": "none",
    "elevation-raised": "0 1px 3px rgba(0, 0, 0, 0.55)",
    "elevation-overlay": "0 8px 24px rgba(0, 0, 0, 0.7)",
    ...AMBIENT_CHROME_DARK,
    ...SEMANTIC_TOKENS,
    ...STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** HIGH-CONTRAST theme — near-black/near-white surfaces, raised contrast, status intact. */
export const HIGH_CONTRAST_THEME: Theme = {
  id: "high-contrast",
  tokens: {
    "color-surface-base": "#000000",
    "color-surface-raised": "#0A0A0A",
    "color-surface-overlay": "#1A1A1A",
    "color-surface-sunken": "#000000",
    "color-interactive-default": "#FFFFFF",
    "color-interactive-hover": "#FFFF00",
    "color-interactive-active": "#00FFFF",
    "color-interactive-disabled": "#666666",
    // TRUE BLACK plate: high-contrast is a DARK theme (surfaces #000, dark-matter basemap) —
    // max-contrast backing under the bright affiliation hues, matching its raised-contrast bar.
    [LABEL_PLATE_TOKEN]: "#000000",
    "elevation-flat": "none",
    "elevation-raised": "0 0 0 1px #FFFFFF",
    "elevation-overlay": "0 0 0 2px #FFFFFF",
    ...AMBIENT_CHROME_DARK,
    ...SEMANTIC_TOKENS,
    ...STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** Bespoke INTEL SKIN — a refined dark tactical command look. Deliberately denser and
 *  higher-contrast than DARK_THEME: near-black cool-neutral surfaces with a clear elevation
 *  ladder, a SINGLE restrained accent — the TEAL SECURE SPINE (#1FB6B6, the refreshed
 *  identity; teal = policy / key access / AI / selection, distinct from every status hue so
 *  the accent never reads as an affiliation) — with hairline borders and tight shadows
 *  instead of heavy chrome. Interactive states ride the one teal hue: a deeper teal at rest
 *  (#0E6E73 "teal deep", the pressed/secure-fill value), the spine at hover (#1FB6B6), and a
 *  light teal at active (#5FE3DE) — one hue brightening, never a second accent. The status-*
 *  affiliation set is the shared APP-6 source of truth (unchanged), so a hostile track is the
 *  same red here as in every other theme. This is the command-grade look the COP shell
 *  applies — intentional, not a kit default. (Canon: tak-design-system Design System.dc.html,
 *  §01 Foundations "Teal is the secure spine".) */
export const INTEL_SKIN_THEME: Theme = {
  id: "intel-skin",
  tokens: {
    // Near-black, faintly cool-neutral surfaces with a deliberate elevation ladder.
    "color-surface-base": "#06090D",
    "color-surface-raised": "#0D1218",
    "color-surface-overlay": "#141B23",
    "color-surface-sunken": "#020406",
    // The single accent: the TEAL SECURE SPINE. Interactive states are one restrained hue,
    // brightening from teal-deep → spine → light — no second accent competes for the eye.
    "color-interactive-default": "#0E6E73",
    "color-interactive-hover": "#1FB6B6",
    "color-interactive-active": "#5FE3DE",
    "color-interactive-disabled": "#2A2F36",
    // Near-black plate riding the intel surface family (#06090D base) — dark basemap.
    [LABEL_PLATE_TOKEN]: "#06090E",
    // Hairline elevation — borders carry the structure, shadows stay tight (no soft glow).
    "elevation-flat": "none",
    "elevation-raised": "0 1px 0 rgba(31, 182, 182, 0.06), 0 2px 8px rgba(0, 0, 0, 0.6)",
    "elevation-overlay": "0 0 0 1px rgba(31, 182, 182, 0.1), 0 12px 32px rgba(0, 0, 0, 0.75)",
    ...AMBIENT_CHROME_DARK,
    ...SEMANTIC_TOKENS,
    ...STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** MONO / NVG (night-vision goggle) theme — the DESIGN-SPEC §2.3 master-test theme.
 *  Hue is deliberately STRIPPED to a single green-phosphor channel so the COP can be
 *  proven hue-independent: if the picture still reads here, meaning lives in SHAPE
 *  (the 2525 affiliation frame drawn by symbology.ts) and not in colour. Channel law:
 *  affiliation = FRAME SHAPE; colour only CONFIRMS, never carries.
 *
 *  The status-* slots are NOT the shared APP-6 palette here — by design. They collapse
 *  to one phosphor hue (#7CFC00-family) split into FIVE DISTINCT LUMINANCE steps, ranked
 *  so the highest-salience affiliations are brightest:
 *    hostile  → brightest phosphor (#CBFFB0)  — must pop even with hue removed
 *    pending  → bright            (#A6F27A)
 *    unknown  → mid              (#7CD94B)
 *    friend   → dim              (#4FA82E)
 *    neutral  → dimmest          (#2E7A1B)
 *  affiliationColorsFor(theme) resolves these via status-* exactly as for every other
 *  theme, so the milsym frame strokes/fills in graded phosphor while the FRAME SHAPE
 *  remains the affiliation carrier. With hue effectively one-dimensional, shape + the
 *  luminance ranking keep the escort/intercept/hostile triad legible. */
const MONO_STATUS_TOKENS: Readonly<Record<string, string>> = Object.freeze({
  "status-hostile": "#CBFFB0",
  "status-friendly": "#4FA82E",
  "status-neutral": "#2E7A1B",
  "status-unknown": "#7CD94B",
  "status-pending": "#A6F27A",
});

export const MONO_NVG_THEME: Theme = {
  id: "mono-nvg",
  tokens: {
    // Black scope with faint green-phosphor lift — the classic NVG/night-watch look.
    "color-surface-base": "#020A02",
    "color-surface-raised": "#061206",
    "color-surface-overlay": "#0A1A08",
    "color-surface-sunken": "#000400",
    // Interactive states ride the phosphor hue, brightening on hover/active.
    "color-interactive-default": "#3FA02A",
    "color-interactive-hover": "#7CFC00",
    "color-interactive-active": "#B6FF7A",
    "color-interactive-disabled": "#1C3315",
    // Phosphor-black plate (== the scope's surface-base) — mono discipline: the plate is
    // luminance-neutral so the graded phosphor label hues keep their ranking on top of it.
    [LABEL_PLATE_TOKEN]: "#020A02",
    // Hairline phosphor-tinted elevation; no soft colour glow (mono discipline).
    "elevation-flat": "none",
    "elevation-raised": "0 1px 0 rgba(124, 252, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.7)",
    "elevation-overlay": "0 0 0 1px rgba(124, 252, 0, 0.12), 0 12px 32px rgba(0, 0, 0, 0.8)",
    // MONO discipline: status-* collapse to a single phosphor hue split by luminance,
    // overriding the shared APP-6 palette so the master hue-strip test can be run.
    ...AMBIENT_CHROME_DARK,
    ...SEMANTIC_TOKENS,
    ...MONO_STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** TRUE LIGHT theme — the 'day' profile. The first shipped theme with LIGHT surfaces: a calm
 *  neutral light ground with near-white panel glass, a dark slate text ramp (AMBIENT_CHROME_LIGHT),
 *  and softened cool-tinted elevation. The single accent stays the TEAL SECURE SPINE, but DEEPENED
 *  so it reads on light (the interactive ramp darkens on press rather than brightening, and the
 *  derived --ui-accent = color-interactive-hover lands at a ~5:1-on-white deep teal). The status-*
 *  affiliation set is the shared APP-6 source of truth (unchanged) so a hostile track is the same
 *  red here as in every dark theme. Pair with the CARTO Positron basemap (see theme.ts
 *  LIGHT_BASEMAP_URL); the MapLibre basemap swap lives in cop.ts setRenderProfile. */
export const LIGHT_THEME: Theme = {
  id: "light",
  tokens: {
    // Calm light surfaces with a deliberate elevation ladder (lightest = the panel glass).
    "color-surface-base": "#E9EDF2",
    "color-surface-raised": "#F3F6F9",
    "color-surface-overlay": "#FAFBFC",
    "color-surface-sunken": "#D5DCE4",
    // The teal secure spine, DEEPENED for light: rest is a mid teal, interaction DARKENS
    // (more saturated = more emphasis on a light ground, the inverse of the dark themes'
    // brighten-on-hover). --ui-accent derives from -hover, so it lands readable-on-white.
    "color-interactive-default": "#0F8A8F",
    "color-interactive-hover": "#0B6E72",
    "color-interactive-active": "#084F52",
    "color-interactive-disabled": "#B8C2CC",
    // WHITE plate for the light/Positron basemap (Will: "~50% opacity white") — dark text is
    // NOT forced (affiliation hue stays the channel); the dark SDF outline carries the edge.
    [LABEL_PLATE_TOKEN]: "#FFFFFF",
    // Soft, cool-tinted elevation — dark heavy shadows would crush a light surface.
    "elevation-flat": "none",
    "elevation-raised": "0 1px 2px rgba(30, 45, 70, 0.12)",
    "elevation-overlay": "0 8px 28px rgba(30, 45, 70, 0.18)",
    ...AMBIENT_CHROME_LIGHT,
    ...SEMANTIC_TOKENS,
    ...STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** TAK theme — a GENERIC ATAK / WinTAK-FAMILIAR palette (deliberately NOT pixel-exact to any
 *  one client). The look an operator coming off ATAK/WinTAK recognises: warm-neutral GRAPHITE
 *  surfaces (greyer/warmer than DARK_THEME's cool slate) with a recognisable TAK-BLUE interactive
 *  ramp (mil-spec command blue, brightening on hover/active — never a second accent). The status-*
 *  affiliation set is the shared APP-6 source of truth (unchanged) so a hostile track is the same
 *  red here as in every other theme — the affiliation FRAME/HUE channel law (CONTRACT:C4) holds.
 *  Pairs with the dark-matter basemap (see theme.ts basemapUrlForProfile). */
export const TAK_THEME: Theme = {
  id: "tak",
  tokens: {
    // Warm-neutral graphite surfaces with a deliberate elevation ladder (TAK's charcoal feel).
    "color-surface-base": "#15181B",
    "color-surface-raised": "#1F2429",
    "color-surface-overlay": "#272D33",
    "color-surface-sunken": "#0C0E10",
    // The single accent: TAK command-blue. Interactive states ride the one hue, brightening
    // from a deep rest → mid hover → light active (no second accent competes for the eye).
    "color-interactive-default": "#2E6FA6",
    "color-interactive-hover": "#3E8AC9",
    "color-interactive-active": "#5AA6E0",
    "color-interactive-disabled": "#363B41",
    // Warm graphite-black plate (== the TAK surface-sunken step) — dark-matter basemap.
    [LABEL_PLATE_TOKEN]: "#0C0E10",
    "elevation-flat": "none",
    "elevation-raised": "0 1px 3px rgba(0, 0, 0, 0.6)",
    "elevation-overlay": "0 8px 24px rgba(0, 0, 0, 0.72)",
    ...AMBIENT_CHROME_DARK,
    ...SEMANTIC_TOKENS,
    ...STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** CIVIC theme — the CONSUMER tier (placeholder name "Civic"; the operator-facing label is
 *  centralised so it is trivial to rename — see SettingsDrawer PROFILES). A calm, approachable
 *  consumer-map look (think a friendly civilian mapping app): clean light surfaces with a vivid
 *  but accessible consumer-blue accent that DARKENS on interaction so it reads on the light ground
 *  (the inverse of the dark themes' brighten-on-hover, mirroring LIGHT_THEME). The status-*
 *  affiliation set is STILL the shared APP-6 source of truth — affiliation semantics never relax
 *  for the consumer tier (a hostile track is the same red). Pairs with the CARTO Positron (light)
 *  basemap (see theme.ts basemapUrlForProfile). */
export const CIVIC_THEME: Theme = {
  id: "civic",
  tokens: {
    // Clean, friendly light surfaces with the panel glass as the lightest step.
    "color-surface-base": "#F1F4F8",
    "color-surface-raised": "#FFFFFF",
    "color-surface-overlay": "#FFFFFF",
    "color-surface-sunken": "#E1E6EC",
    // Consumer blue, DEEPENED for light: rest is a vivid blue, interaction DARKENS (more
    // saturated = more emphasis on a light ground). --ui-accent derives from -hover, so it
    // lands readable-on-white (the same light-theme discipline LIGHT_THEME uses).
    "color-interactive-default": "#3B6FE0",
    "color-interactive-hover": "#2B57BE",
    "color-interactive-active": "#1E40A0",
    "color-interactive-disabled": "#C4CCD8",
    // WHITE plate — Civic pairs with the light Positron basemap exactly like LIGHT_THEME.
    [LABEL_PLATE_TOKEN]: "#FFFFFF",
    "elevation-flat": "none",
    "elevation-raised": "0 1px 2px rgba(30, 45, 70, 0.1)",
    "elevation-overlay": "0 8px 28px rgba(30, 45, 70, 0.16)",
    ...AMBIENT_CHROME_LIGHT,
    ...SEMANTIC_TOKENS,
    ...STATUS_TOKENS,
    ...STRUCTURAL_TOKENS,
  },
};

/** The shipped theme registry, keyed by theme id. */
export const THEMES: Readonly<Record<string, Theme>> = Object.freeze({
  [DARK_THEME.id]: DARK_THEME,
  [HIGH_CONTRAST_THEME.id]: HIGH_CONTRAST_THEME,
  [INTEL_SKIN_THEME.id]: INTEL_SKIN_THEME,
  [MONO_NVG_THEME.id]: MONO_NVG_THEME,
  [LIGHT_THEME.id]: LIGHT_THEME,
  [TAK_THEME.id]: TAK_THEME,
  [CIVIC_THEME.id]: CIVIC_THEME,
});

/** Configuration default (THEME_DEFAULT) — the dark tactical look. */
export const DEFAULT_THEME_ID = "dark";
